import { Add, InfoRounded } from "@mui/icons-material";
import { Alert, Box, Button, Stack, Typography } from "@mui/joy";
import { Link } from "@tanstack/react-router";
import { ApiCategory } from "griptonite-api-client";
import { useState } from "react";

import { useCompetitionQuery } from "@/hooks/competitions";
import capitalize from "@/utils/capitalize";

import BottomButtons from "./BottomButtons";
import CompetitionCategoryDrawer from "./CompetitionCategoryDrawer";
import { useUnhidePreviousTabWarningsOnMount } from "./getPublishWarnings";
import PublishWarningsAlert from "./PublishWarningsAlert";

export default function CompetitionCategories({ competitionUuid }: { competitionUuid: string }) {
  const { data: competition } = useCompetitionQuery(competitionUuid);

  const [sidebarState, setSidebarState] = useState<{
    category: ApiCategory | null;
  } | null>(null);

  useUnhidePreviousTabWarningsOnMount(competitionUuid, "details");

  return (
    <>
      <Stack flex={1} sx={{ overflowY: "auto" }}>
        <Box sx={{ flex: 1, padding: 1.5 }}>
          <PublishWarningsAlert competition={competition} tab="categories" />
          {competition?.league ? (
            <Alert color="neutral" sx={{ marginBottom: 1.5 }}>
              <InfoRounded />
              <Typography level="body-sm">
                This competition is part of a series, so you can’t add or remove categories here.{" "}
                <br />
                Instead, edit the categories{" "}
                <Link
                  from="/gym/$venueUuid"
                  to="/gym/$venueUuid/events/series/$leagueUuid/profile/categories"
                  params={{
                    leagueUuid: competition.league.uuid,
                  }}
                >
                  on the series level
                </Link>
                .
              </Typography>
            </Alert>
          ) : null}
          <CategoriesList
            categories={competition?.stages[0].categories}
            onCategoryClick={(category) => !competition?.league && setSidebarState({ category })}
          />
          <Box sx={{ marginTop: 1.5 }}>
            <Button
              startDecorator={<Add />}
              onClick={() =>
                setSidebarState({
                  category: null,
                })
              }
              disabled={!!competition?.league}
            >
              Add Category
            </Button>
          </Box>
        </Box>

        <Box sx={{ padding: 1.5 }}>
          <BottomButtons
            competition={competition}
            nextLink={{
              to: "/gym/$venueUuid/events/$competitionUuid/profile/circuits",
            }}
            prevLink={{
              to: "/gym/$venueUuid/events/$competitionUuid/profile/details",
            }}
          />
        </Box>
      </Stack>
      {/* <Drawer open={sidebarState !== null} anchor="right">

      </Drawer> */}
      <CompetitionCategoryDrawer
        open={sidebarState !== null}
        onClose={() => setSidebarState(null)}
        competition={competition}
        category={sidebarState?.category ?? undefined}
      />
    </>
  );
}

export const CategoriesList = ({
  categories,
  onCategoryClick,
}: {
  categories?: ApiCategory[];
  onCategoryClick: (category: ApiCategory) => void;
}) => {
  if (!categories || categories.length === 0) {
    return <Typography>No categories yet.</Typography>;
  }
  return (
    <Stack spacing={1}>
      {categories?.map((category) => (
        <CategoryRow
          key={category.uuid}
          category={category}
          onClick={() => onCategoryClick(category)}
        />
      ))}
    </Stack>
  );
};

const CategoryRow = ({ category, onClick }: { category: ApiCategory; onClick: () => void }) => {
  const genderLabel = {
    MALE: "Male",
    FEMALE: "Female",
    "": "Everyone",
  }[category.gender || ""];

  let ageLabel = "All ages";
  if (category.minAge && category.maxAge) {
    ageLabel = `${category.minAge} - ${category.maxAge}`;
  } else if (category.minAge) {
    ageLabel = `${category.minAge} and over`;
  } else if (category.maxAge) {
    ageLabel = `${category.maxAge} and under`;
  }

  let abilityLabel = "All abilities";
  if (category.abilityCategories) {
    abilityLabel = category.abilityCategories.map((ac) => capitalize(ac.toLowerCase())).join(", ");
  }

  return (
    <Stack
      direction="row"
      sx={{
        borderRadius: 8,
        border: "1px solid",
        borderColor: "neutral.200",
        padding: 1,
        paddingY: 0.5,
        cursor: "pointer",
      }}
      alignItems="center"
      gap={2}
      onClick={onClick}
    >
      <Typography
        level="title-md"
        flex={1}
        startDecorator={
          <Box
            sx={{
              backgroundColor: category.color,
              width: 16,
              height: 16,
              borderRadius: 8,
            }}
          />
        }
      >
        {category.name}
      </Typography>
      <Typography level="body-sm" color="neutral">
        Gender: {genderLabel}
      </Typography>
      <Typography level="body-sm" color="neutral">
        Age: {ageLabel}
      </Typography>
      {category.abilityCategories && (
        <Typography level="body-sm" color="neutral">
          Abilities: {abilityLabel}
        </Typography>
      )}
    </Stack>
  );
};
